import { useState, useEffect } from "react";
import { ExternalLink, Shield } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SponsorSidebar({ pageType = "all", cityName = "" }) {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/banners`).then(r => {
      const filtered = r.data.filter(b => b.placement === "sidebar" && (b.page_type === "all" || b.page_type === pageType));
      setBanners(filtered);
    }).catch(() => {});
  }, [pageType]);

  return (
    <div className="space-y-4" data-testid="sponsor-sidebar">
      {/* AdSense — Single large slot */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 text-center shadow-sm">
        <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
        <div className="bg-slate-50 rounded-lg py-20 px-6 border border-dashed border-slate-200">
          <p className="text-xs text-slate-400">Google AdSense</p>
          <p className="text-[10px] text-slate-300">300x600</p>
        </div>
      </div>

      {/* CreditCane — Owner's business */}
      <a href="https://creditcane.com" target="_blank" rel="noopener" className="no-underline block">
        <div className="p-5 bg-gradient-to-br from-sky-50 via-cyan-50 to-sky-100 rounded-xl border-2 border-sky-200 hover:shadow-md transition-all">
          <p className="text-sm font-bold text-sky-900 mb-1">Bad Credit? We Can Help</p>
          <p className="text-xs text-slate-600 mb-3">Free credit repair consultation. Boost your score and build your future.</p>
          <span className="inline-block text-xs font-semibold text-white bg-sky-700 px-3 py-1.5 rounded-md">Fix My Credit</span>
        </div>
      </a>

      {/* Dynamic banners from admin — YOUR custom ads */}
      {banners.map(b => (
        <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Sponsored</p>
          <div dangerouslySetInnerHTML={{ __html: b.html_content }} />
        </div>
      ))}
    </div>
  );
}

export function InlineBanner({ cityName = "" }) {
  return (
    <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-100 rounded-xl p-6 my-8" data-testid="inline-banner">
      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="flex-1">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Sponsored Partner</p>
          <p className="text-sm font-semibold text-slate-900 mb-1">
            Moving to {cityName || "a new city"}? Get the best credit card for your move.
          </p>
          <p className="text-xs text-slate-500">
            Earn cashback on moving expenses, groceries, and gas. Top-rated cards with $200+ sign-up bonuses.
          </p>
        </div>
        <a href="mailto:support@lifeownit.com?subject=Affiliate%20Inquiry"
          className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-700 text-white rounded-lg text-xs font-semibold hover:bg-emerald-800 transition-colors no-underline whitespace-nowrap"
          data-testid="inline-banner-cta">
          Compare Cards <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

export function BottomAdBanner() {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    axios.get(`${API}/banners`).then(r => {
      const bottomBanner = r.data.find(b => b.placement === "bottom" && b.active);
      if (bottomBanner) setBanner(bottomBanner);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-slate-50 border-t border-slate-200 py-4" data-testid="bottom-ad-banner">
      <div className="container-main text-center">
        {banner ? (
          <div>
            <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
            <div dangerouslySetInnerHTML={{ __html: banner.html_content }} />
          </div>
        ) : (
          <div>
            <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
            <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl mx-auto">
              <p className="text-xs text-slate-400">Google AdSense Ad Space</p>
              <p className="text-[10px] text-slate-300">Pending Google approval</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SidebarAds() {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/banners`).then(r => {
      setBanners(r.data.filter(b => b.placement === "sidebar" && b.active));
    }).catch(() => {});
  }, []);

  return (
    <div className="sticky-sidebar space-y-4" data-testid="sidebar-ads">
      {/* AdSense — Single large slot */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 text-center">
        <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
        <div className="bg-slate-50 rounded-lg py-20 px-6 border border-dashed border-slate-200">
          <p className="text-xs text-slate-400">Google AdSense</p>
          <p className="text-[10px] text-slate-300">300x600</p>
        </div>
      </div>

      {/* CreditCane — Owner's business */}
      <a href="https://creditcane.com" target="_blank" rel="noopener" className="no-underline block" data-testid="sidebar-creditcane">
        <div className="p-5 bg-gradient-to-br from-sky-50 via-cyan-50 to-sky-100 rounded-xl border-2 border-sky-200 hover:shadow-md transition-all">
          <p className="text-sm font-bold text-sky-900 mb-1">Bad Credit? We Can Help</p>
          <p className="text-xs text-slate-600 mb-3">Free credit repair consultation. Boost your score, eliminate debt, and build your future.</p>
          <span className="inline-block text-xs font-semibold text-white bg-sky-700 px-3 py-1.5 rounded-md">Fix My Credit</span>
        </div>
      </a>

      {/* Dynamic banners from admin — YOUR custom ads go here */}
      {banners.map(b => (
        <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 mb-2">Sponsored</p>
          <div dangerouslySetInnerHTML={{ __html: b.html_content }} />
        </div>
      ))}
    </div>
  );
}
